import { Client } from '../types/GrooverTypes'

/**
 * Resolves the display name for a room.
 *
 * Prefers the live name from clientData.locations (via roomId) so that room
 * renames are reflected immediately without requiring activity documents to be
 * re-saved. Falls back to the legacy activity.place string when roomId is
 * absent or not found.
 */
export const getRoomNameById = (
  roomId: string | undefined,
  clientData: Client | null | undefined
): string | undefined => {
  if (!roomId || !clientData?.locations) return undefined
  for (const loc of clientData.locations) {
    const room = loc.rooms?.find((r) => r.id === roomId)
    if (room) return room.name
  }
  return undefined
}

/**
 * Returns the best available room display name for an activity.
 * Looks up the current name via roomId first, then falls back to activity.place.
 */
export const resolveActivityRoomName = (
  activity: { roomId?: string; place?: string },
  clientData: Client | null | undefined
): string => {
  return getRoomNameById(activity.roomId, clientData) ?? activity.place ?? ''
}

/**
 * Resolves the display name for a location (venue/premises).
 *
 * Prefers the live name from clientData.locations (via locationId) so that
 * venue renames are reflected immediately. Returns undefined when the location
 * cannot be resolved.
 */
export const getLocationNameById = (
  locationId: string | undefined,
  clientData: Client | null | undefined
): string | undefined => {
  if (!locationId || !clientData?.locations) return undefined
  const name = clientData.locations.find((loc) => loc.id === locationId)?.name?.trim()
  return name || undefined
}

/**
 * Street address from clientData.locations (via locationId).
 * Location.adress is the persisted field name. Empty/whitespace is treated as missing.
 */
export const getLocationAddressById = (
  locationId: string | undefined,
  clientData: Client | null | undefined
): string | undefined => {
  if (!locationId || !clientData?.locations) return undefined
  const adress = clientData.locations.find((loc) => loc.id === locationId)?.adress?.trim()
  return adress || undefined
}

/**
 * Venue/premises name for receipt and registration confirmation "Lokal".
 * Prefers the live location name (admin Lokal dropdown), then room/place.
 */
export const resolveActivityLocationName = (
  activity: { locationId?: string; roomId?: string; place?: string },
  clientData: Client | null | undefined
): string => {
  return getLocationNameById(activity.locationId, clientData) || resolveActivityRoomName(activity, clientData)
}

/**
 * Receipt/confirmation Lokal + Adress. Address is only returned when Lokal
 * resolved, so the UI never shows "N/A" next to a street.
 */
export const resolveActivityLocationReceiptFields = (
  activity: { locationId?: string; roomId?: string; place?: string },
  clientData: Client | null | undefined
): { locationDisplayName: string; locationAddress?: string } => {
  const locationDisplayName = resolveActivityLocationName(activity, clientData)
  if (!locationDisplayName) {
    return { locationDisplayName: '' }
  }
  return {
    locationDisplayName,
    locationAddress: getLocationAddressById(activity.locationId, clientData)
  }
}

/**
 * Venue name + Google Maps URL for activity-start reminder emails.
 * Empty strings when missing so SendGrid `{{#if locationName}}` / `{{#if googleMapsUrl}}` hide the block.
 */
export const resolveActivityLocationEmailFields = (
  activity: { locationId?: string },
  client: Client | null | undefined
): { locationName: string; googleMapsUrl: string } => {
  const loc = client?.locations?.find((l) => l.id === activity.locationId)
  return {
    locationName: loc?.name ?? '',
    googleMapsUrl: loc?.googleUrl ?? ''
  }
}
